// src/PDF/InvoicePDF.jsx
import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

const money = (n) => {
  const v = Number(n || 0);
  return `₦${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safe = (x) => (x ? String(x) : "—");

const isPaidStatus = (s) => {
  const v = String(s || "")
    .toLowerCase()
    .trim();
  return (
    v === "paid" || v === "settled" || v === "complete" || v === "completed"
  );
};

// React-PDF supports absolute-position overlays on the page.
// Keep it light-themed and not too aggressive.
const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10.5,
    color: "#111827",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },

  // PAID stamp overlay
  paidStampWrap: {
    position: "absolute",
    top: 130,
    left: 0,
    right: 0,
    alignItems: "center",
    pointerEvents: "none",
  },
  paidStamp: (accent) => ({
    borderWidth: 3,
    borderColor: accent,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 10,
    transform: "rotate(-12deg)",
    opacity: 0.22,
  }),
  paidStampText: (accent) => ({
    color: accent,
    fontSize: 34,
    fontWeight: 800,
    letterSpacing: 2,
  }),

  // Header
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 16,
  },
  brandLeft: { flex: 1 },
  titleRight: { width: 220, alignItems: "flex-end" },
  logo: { width: 84, height: 36, objectFit: "contain", marginBottom: 8 },
  h1: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  companyName: { fontSize: 12.5, fontWeight: 700, marginBottom: 4 },

  smallMuted: { color: "#6B7280" },
  strong: { fontWeight: 700 },

  // Cards/blocks
  block: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  blockTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 8,
  },

  // Two column info
  twoCol: { flexDirection: "row", gap: 12 },
  col: { flex: 1 },

  kvRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    gap: 10,
  },
  kvKey: { color: "#374151" },
  kvVal: { fontWeight: 700, maxWidth: "65%", textAlign: "right" },

  // Table
  table: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 12,
  },
  thead: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  th: { fontWeight: 700, color: "#111827" },

  // IMPORTANT: allow clean pagination
  tbodyRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },

  cDesc: { flex: 1.9, paddingRight: 8 },
  cQty: { width: 52, textAlign: "right" },
  cRate: { width: 86, textAlign: "right" },
  cAmt: { width: 92, textAlign: "right" },

  // Totals
  totalsWrap: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 2,
  },
  totalsBox: {
    width: 260,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  totalLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  grandLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 8,
    marginTop: 6,
  },
  grandText: { fontSize: 12, fontWeight: 800 },

  // Accent pill
  statusPill: (accent) => ({
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: accent,
    backgroundColor: "#ffffff",
    color: accent,
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 999,
    fontSize: 9.5,
    fontWeight: 700,
    marginTop: 6,
  }),

  // Payment block
  payRow: {
    flexDirection: "row",
    gap: 12,
  },
  payCol: { flex: 1 },
  payLine: { marginBottom: 4, color: "#374151" },

  footer: {
    marginTop: 14,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    textAlign: "center",
    color: "#6B7280",
  },
});

// Helps pagination: keep header repeating nicely when table flows across pages.
const TableHeader = () => (
  <View style={styles.thead} fixed>
    <Text style={[styles.th, styles.cDesc]}>Description</Text>
    <Text style={[styles.th, styles.cQty]}>Qty</Text>
    <Text style={[styles.th, styles.cRate]}>Unit Price</Text>
    <Text style={[styles.th, styles.cAmt]}>Total</Text>
  </View>
);

export default function InvoicePDF({
  companyInfo,
  invoiceData,
  items,
  accent = "#008080",
}) {
  const subtotal = Number(invoiceData?.total || 0);
  const vatRate = Number(invoiceData?.vat_rate || 0);
  const vat = subtotal * vatRate;
  const total = subtotal + vat;

  const statusRaw = invoiceData?.status || "open";
  const paid = isPaidStatus(statusRaw);
  const statusLabel = String(statusRaw).toUpperCase();

  const bankName = companyInfo?.bank_name || companyInfo?.bankName;
  const accountName = companyInfo?.account_name || companyInfo?.accountName;
  const accountNumber =
    companyInfo?.account_number || companyInfo?.accountNumber;
  const paymentInstructions =
    companyInfo?.payment_instructions || companyInfo?.paymentInstructions || "";

  return (
    <Document title={safe(invoiceData?.invoiceNumber)}>
      <Page size="A4" style={styles.page}>
        {/* PAID stamp overlay */}
        {paid ? (
          <View style={styles.paidStampWrap}>
            <View style={styles.paidStamp(accent)}>
              <Text style={styles.paidStampText(accent)}>PAID</Text>
            </View>
          </View>
        ) : null}

        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.brandLeft}>
            {companyInfo?.logo_url ? (
              <Image style={styles.logo} src={companyInfo.logo_url} />
            ) : null}

            <Text style={styles.companyName}>
              {safe(companyInfo?.company_name)}
            </Text>
            <Text style={styles.smallMuted}>{safe(companyInfo?.address)}</Text>

            <Text style={styles.smallMuted}>
              {companyInfo?.phone_number
                ? `Phone: ${companyInfo.phone_number}`
                : ""}
              {companyInfo?.phone_number && companyInfo?.email ? "  •  " : ""}
            </Text>
            <Text style={styles.smallMuted}>{safe(companyInfo?.email)}</Text>

            {companyInfo?.TIN ? (
              <Text style={styles.smallMuted}>TIN: {companyInfo.TIN}</Text>
            ) : null}
          </View>

          <View style={styles.titleRight}>
            <Text style={styles.h1}>INVOICE</Text>

            <Text style={{ fontSize: 11 }}>
              <Text style={styles.smallMuted}>Invoice #</Text>{" "}
              <Text style={styles.strong}>
                {safe(invoiceData?.invoiceNumber)}
              </Text>
            </Text>

            <Text style={{ fontSize: 10.5, marginTop: 4 }}>
              <Text style={styles.smallMuted}>Date Issued:</Text>{" "}
              <Text style={styles.strong}>{safe(invoiceData?.date)}</Text>
            </Text>

            <Text style={{ fontSize: 10.5 }}>
              <Text style={styles.smallMuted}>Due Date:</Text>{" "}
              <Text style={styles.strong}>{safe(invoiceData?.expiryDate)}</Text>
            </Text>

            <Text style={styles.statusPill(accent)}>{statusLabel}</Text>
          </View>
        </View>

        {/* Recipient */}
        <View style={styles.twoCol}>
          <View style={[styles.block, styles.col]}>
            <Text style={styles.blockTitle}>Recipient Information</Text>
            <View style={styles.kvRow}>
              <Text style={styles.kvKey}>Name</Text>
              <Text style={styles.kvVal}>{safe(invoiceData?.customer)}</Text>
            </View>
            <View style={styles.kvRow}>
              <Text style={styles.kvKey}>Address</Text>
              <Text style={styles.kvVal}>{safe(invoiceData?.address)}</Text>
            </View>
          </View>
        </View>

        {/* Items */}
        <Text style={{ fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
          Description of Services Provided
        </Text>

        <View style={styles.table}>
          <TableHeader />

          {(items || []).length ? (
            (items || []).map((it, idx) => (
              // Wrap=false prevents splitting a row across pages.
              // If a row is too tall, React-PDF will move it to next page.
              <View key={idx} style={styles.tbodyRow} wrap={false}>
                <Text style={styles.cDesc}>{safe(it.description)}</Text>
                <Text style={styles.cQty}>{Number(it.quantity || 0)}</Text>
                <Text style={styles.cRate}>{money(it.unitPrice)}</Text>
                <Text style={styles.cAmt}>{money(it.lineTotal)}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tbodyRow}>
              <Text style={{ color: "#6B7280" }}>No items.</Text>
            </View>
          )}
        </View>

        {/* Notes + Totals */}
        <View style={styles.twoCol}>
          <View style={[styles.block, styles.col]}>
            <Text style={styles.blockTitle}>Terms & Notes</Text>
            <Text style={{ color: "#374151", lineHeight: 1.4 }}>
              {invoiceData?.notes
                ? String(invoiceData.notes)
                : "Please include the invoice number in your payment reference. If you have questions regarding this invoice, please contact us."}
            </Text>
          </View>

          <View style={styles.totalsWrap}>
            <View style={styles.totalsBox}>
              <View style={styles.totalLine}>
                <Text style={styles.smallMuted}>Subtotal</Text>
                <Text style={styles.strong}>{money(subtotal)}</Text>
              </View>
              <View style={styles.totalLine}>
                <Text style={styles.smallMuted}>VAT ({vatRate * 100}%)</Text>
                <Text style={styles.strong}>{money(vat)}</Text>
              </View>
              <View style={styles.grandLine}>
                <Text style={styles.grandText}>Total</Text>
                <Text style={[styles.grandText, { color: accent }]}>
                  {money(total)}
                </Text>
              </View>
            </View>
          </View>
          {/* Payment instructions */}
        </View>
        <View style={styles.block}>
          <Text style={styles.blockTitle}>Payment Instructions</Text>

          <View style={styles.payRow}>
            <View style={styles.payCol}>
              <Text style={styles.payLine}>
                <Text style={styles.smallMuted}>Bank Name:</Text>{" "}
                {safe(bankName)}
              </Text>
              <Text style={styles.payLine}>
                <Text style={styles.smallMuted}>Account Name:</Text>{" "}
                {safe(accountName)}
              </Text>
              <Text style={styles.payLine}>
                <Text style={styles.smallMuted}>Account Number:</Text>{" "}
                {safe(accountNumber)}
              </Text>
              <Text style={styles.payLine}>
                <Text style={styles.smallMuted}>Payment Instructions:</Text>{" "}
                {safe(paymentInstructions)}
              </Text>
            </View>
          </View>
        </View>

        <Text style={styles.footer}>Thank you for your business.</Text>
      </Page>
    </Document>
  );
}
